import { useState, useCallback } from 'react';

export interface PaginationState {
  page: number;
  limit: number;
  hasMore: boolean;
  loading: boolean;
  totalCount: number;
}

export interface UsePaginationOptions {
  initialLimit?: number;
  onLoadMore?: (page: number, limit: number) => Promise<any>;
}

export function usePagination(options: UsePaginationOptions = {}) {
  const { initialLimit = 20, onLoadMore } = options;
  
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: initialLimit,
    hasMore: true,
    loading: false,
    totalCount: 0,
  });

  const loadMore = useCallback(async () => {
    if (pagination.loading || !pagination.hasMore) return;

    setPagination(prev => ({ ...prev, loading: true }));

    try {
      const nextPage = pagination.page + 1;
      const result = await onLoadMore?.(nextPage, pagination.limit);
      
      setPagination(prev => ({
        ...prev,
        page: nextPage,
        loading: false,
        hasMore: result?.hasMore ?? false,
        totalCount: result?.totalCount ?? prev.totalCount,
      }));

      return result;
    } catch (error) {
      setPagination(prev => ({ ...prev, loading: false }));
      throw error;
    }
  }, [pagination.page, pagination.limit, pagination.loading, pagination.hasMore, onLoadMore]);

  const reset = useCallback(() => {
    setPagination({
      page: 1,
      limit: initialLimit,
      hasMore: true,
      loading: false,
      totalCount: 0,
    });
  }, [initialLimit]);

  const updateTotalCount = useCallback((totalCount: number) => {
    setPagination(prev => ({
      ...prev,
      totalCount,
      hasMore: prev.page * prev.limit < totalCount,
    }));
  }, []);

  return {
    pagination,
    loadMore,
    reset,
    updateTotalCount,
  };
}
